import { RemoteCartInfo } from '@@types'
import { type ApolloClient, gql } from '@apollo/client'

import type {
  CartInfoInput,
  ServerCart,
  StockProductInput,
  SyncCartResult,
} from '../types'

export const LOAD_CARTS_QUERY = gql`
  query LoadCarts {
    loadCarts {
      shop_id
      items {
        product_id
        amount
        comment
        offer_id
      }
    }
  }
`

export const SYNC_CART_MUTATION = gql`
  mutation SyncCart($cart: CartInfoInput!) {
    syncCart(cart: $cart) {
      id
      product_id
      amount
      shop_id
      delivery_cost
      usedPromocode
    }
  }
`

export const INITIAL_SYNC_CARTS_MUTATION = gql`
  mutation InitialSyncCarts($carts: [CartInfoInput]!) {
    initialSyncCarts(carts: $carts) {
      id
      product_id
      amount
      shop_id
      delivery_cost
      usedPromocode
    }
  }
`

export const CHECK_PRODUCTS_IN_STOCK_QUERY = gql`
  query CheckProductsInStock($productData: StockProductInput!) {
    checkProductsInStock(productData: $productData) {
      error_code
      message
      max_amount
      productInfo {
        id
        name
      }
      product_id
    }
  }
`

export const CART_INFO_QUERY = gql`
  query CartInfo(
    $cartInfo: CartInfoInput!
    $style: String
    $imageStyle2: String
    $imagesStyle2: String
    $previewStyle2: String
  ) {
    cartInfo(cartInfo: $cartInfo) {
      totalWeight
      promocode
      promocodeType
      priceGroup
      totals {
        count
        baseTotal
        total
        baseSale
        sale
        salePercent
        maxSale
        promoPercent
        effectiveTotal
      }
      delivery {
        total
        sale
        salePercent
        totalPercent
        maxSale
        promoPercent
        type
        from
        timeRestrictions {
          from
          to
        }
      }
      products {
        id
        amount
        shop_id
        total
        comment
        info {
          id
          name
          price
          oldPrice
          price_type
          isWeightProduct
          count_multiplier
          isFavorite
          stock {
            amount
          }
          preview(style: $previewStyle2) {
            url
          }
          slug
          review_count
          is_alcohol
          categories {
            id
            name
            slug
            parent_id
          }
          priceOffers {
            id
            price
            oldPrice
            salePeriod {
              start
              finish
            }
            min
            max
            step
            quant
            uiQuant
            quantAmount
            isDefault
            group
          }
          priceOffer @client {
            id
            price
            oldPrice
            count_multiplier
            salePeriod {
              start
              finish
            }
            min
            max
            step
            quant
            quantAmount
            isDefault
            group
          }
        }
      }
      sales {
        salePluginId
        bonusCurrencyId
        amount
        sale
        salePercent
        type
      }
      saleOffers {
        id
        title
        reason
        reasonId
        type
        discount
        maxDiscount
        discountCurrencyId
        salePluginId
      }
      saleErrors {
        internalCode
        internalIssuer
        type
        message
        inputPart {
          spendBonuses {
            bonusCurrencyId
            amount
          }
          coupons {
            barcode
          }
          applySales {
            saleId
          }
          promocodes {
            code
          }
        }
      }
    }
  }
`

export interface CartInfoQueryVariables {
  cartInfo: CartInfoInput
  style?: string
  imageStyle2?: string
  imagesStyle2?: string
  previewStyle2?: string
}

export interface CartInfoQueryResult {
  data: {
    cartInfo: RemoteCartInfo | null
  }
}

export interface LoadCartsResponse {
  loadCarts: ServerCart[]
}

export interface SyncCartVariables {
  cart: CartInfoInput
}

export interface SyncCartResponse {
  syncCart: SyncCartResult[]
}

export interface InitialSyncCartsVariables {
  carts: CartInfoInput[]
}

export interface InitialSyncCartsResponse {
  initialSyncCarts: SyncCartResult[]
}

export interface CheckProductsInStockVariables {
  productData: StockProductInput
}

export interface CheckProductsInStockResponse {
  checkProductsInStock: CheckProductsInStockItem[]
}

export interface CheckProductsInStockItem {
  product_id: string
  error_code: number
  message?: string
  max_amount: number
  productInfo?: {
    id: string
    name: string
  }
}

export async function loadCarts (client: ApolloClient<unknown>) {
  return client.query<LoadCartsResponse>({
    query: LOAD_CARTS_QUERY,
    fetchPolicy: 'no-cache',
  })
}

export async function syncCart (client: ApolloClient<unknown>, variables: SyncCartVariables) {
  return client.mutate<SyncCartResponse, SyncCartVariables>({
    mutation: SYNC_CART_MUTATION,
    variables,
  })
}

export async function initialSyncCarts (
  client: ApolloClient<unknown>,
  variables: InitialSyncCartsVariables,
) {
  // Фильтруем корзины с пустым или отсутствующим shop_id
  const filteredCarts = variables.carts.filter((cart) => cart.shop_id && String(cart.shop_id).trim() !== '')

  if (filteredCarts.length === 0) {
    return { data: { initialSyncCarts: [] } }
  }

  return client.mutate<InitialSyncCartsResponse, InitialSyncCartsVariables>({
    mutation: INITIAL_SYNC_CARTS_MUTATION,
    variables: { carts: filteredCarts },
  })
}

export async function checkProductsInStock (
  client: ApolloClient<unknown>,
  variables: CheckProductsInStockVariables,
) {
  return client.query<CheckProductsInStockResponse, CheckProductsInStockVariables>({
    query: CHECK_PRODUCTS_IN_STOCK_QUERY,
    variables,
    fetchPolicy: 'no-cache',
  })
}
